/**
 * BlueBuck Collection Grid JavaScript Component
 */
(function() {
  'use strict';

  function initSection(sectionEl) {
    if (!sectionEl || sectionEl.dataset.initialized === 'true') return;
    sectionEl.dataset.initialized = 'true';

    const sectionId = sectionEl.getAttribute('data-section-id') || sectionEl.id.replace('BbCollectionSection-', '');
    const sortSelect = document.getElementById(`bbSortSelect-${sectionId}`);
    const defaultSort = (sortSelect && sortSelect.value) || 'featured';

    let currentFilter = 'all';
    let currentSort = defaultSort;
    let inStockOnly = true;
    let selectedCats = ['girls', 'boys', 't-shirts', 'shorts', 'sets'];
    let selectedAges = [];
    let minPrice = 0;
    let maxPrice = 5000;
    let activeMediaIndex = 0;
    let currentQvItem = null;
    let priceDebounceTimer = null;
    let isFetching = false;

    function getWishlist() {
      try { return JSON.parse(localStorage.getItem('bb_wishlist')) || []; } catch(e) { return []; }
    }
    function setWishlist(items) {
      try { localStorage.setItem('bb_wishlist', JSON.stringify(items)); } catch(e) {}
    }

    // Helper: Normalize and parse age string into standard tokens for matching
    function getAgeTokens(ageStr) {
      if (!ageStr) return [];
      const clean = ageStr.toLowerCase().trim();
      const tokens = [clean, clean.replace(/\s+/g, '-'), clean.replace(/\s+/g, '_'), clean.replace(/-/g, ' ')];

      // Extract numbers range e.g. "11-12", "11–12", "11 to 12"
      const match = clean.match(/(\d+)\s*[-–—to]+\s*(\d+)/i);
      if (match) {
        const min = match[1];
        const max = match[2];
        tokens.push(
          `${min}-${max}`,
          `${min}–${max}`,
          `${min} - ${max}`,
          `${min}_${max}`,
          `${min} to ${max}`,
          `${min}to${max}`,
          `${min}-${max} yrs`,
          `${min}-${max}yrs`,
          `${min}-${max} years`,
          `${min}-${max}years`,
          `${min}-${max}y`,
          `${min}y-${max}y`
        );
      } else {
        const singleMatch = clean.match(/(\d+)/);
        if (singleMatch) {
          const num = singleMatch[1];
          tokens.push(`${num} yrs`, `${num}yrs`, `${num} years`, `${num}y`);
        }
      }
      return Array.from(new Set(tokens.map(t => t.toLowerCase().trim())));
    }

    // Helper: Check if card matches active age filters
    function matchesSelectedAges(card, activeAges) {
      if (!activeAges || activeAges.length === 0) return true;

      const cardAge = (card.getAttribute('data-age') || '').toLowerCase();
      const cardTags = (card.getAttribute('data-tags') || '').toLowerCase();
      const cardOptions = (card.getAttribute('data-options') || '').toLowerCase();
      const cardSizes = (card.getAttribute('data-sizes') || '').toLowerCase();
      const cardVariants = (card.getAttribute('data-variants') || '').toLowerCase();
      const cardTitle = (card.getAttribute('data-title') || '').toLowerCase();
      const combinedText = `${cardAge} ${cardSizes} ${cardTags} ${cardOptions} ${cardVariants} ${cardTitle}`;

      return activeAges.some(selAge => {
        const selTokens = getAgeTokens(selAge);
        return selTokens.some(token => combinedText.includes(token));
      });
    }

    // Helper: Check if card matches active size filters
    function matchesSize(card, activeSizes) {
      if (!activeSizes || activeSizes.length === 0) return true;

      const cardSizes = (card.getAttribute('data-sizes') || '').toLowerCase();
      const cardOpts = (card.getAttribute('data-options') || '').toLowerCase();
      const cardVars = (card.getAttribute('data-variants') || '').toLowerCase();
      const cardTags = (card.getAttribute('data-tags') || '').toLowerCase();
      const cardAge = (card.getAttribute('data-age') || '').toLowerCase();
      const cardTitle = (card.getAttribute('data-title') || '').toLowerCase();
      const combined = `${cardSizes} ${cardOpts} ${cardVars} ${cardTags} ${cardAge} ${cardTitle}`;

      return activeSizes.some(sz => {
        const raw = sz.toLowerCase().trim();
        // 1. If it's an age-bracket-like size
        if (/\d/.test(raw) || raw.includes('yr') || raw.includes('year') || raw.includes('month') || raw.includes('–') || raw.includes('-')) {
          const tokens = getAgeTokens(raw);
          if (tokens.some(t => combined.includes(t))) return true;
        }

        // 2. Direct inclusion or normalized match
        const szNorm = raw.replace(/[\s–—_-]+/g, '');
        const combinedNorm = combined.replace(/[\s–—_-]+/g, '');
        if (combinedNorm.includes(szNorm)) return true;

        // 3. For letter/number sizes (S, M, L, XL, etc.)
        const regex = new RegExp('(?:^|[\\s/,.-])' + raw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(?:$|[\\s/,.-])', 'i');
        return regex.test(cardSizes) || regex.test(cardOpts) || regex.test(cardVars) || regex.test(cardTags);
      });
    }

    // Helper: Check if card matches active color filters
    function matchesColor(card, activeColors) {
      if (!activeColors || activeColors.length === 0) return true;

      const cardColors = (card.getAttribute('data-colors') || '').toLowerCase();
      const cardOpts = (card.getAttribute('data-options') || '').toLowerCase();
      const cardVars = (card.getAttribute('data-variants') || '').toLowerCase();
      const cardTags = (card.getAttribute('data-tags') || '').toLowerCase();
      const cardTitle = (card.getAttribute('data-title') || '').toLowerCase();
      const combined = `${cardColors} ${cardOpts} ${cardVars} ${cardTags} ${cardTitle}`;

      return activeColors.some(col => {
        const c = col.toLowerCase().trim();
        return combined.includes(c);
      });
    }

    // Build standard Storefront Search URL from current form & active options
    function getFilterFormUrl() {
      const form = document.getElementById(`bbFacetFiltersForm-${sectionId}`);
      const base = (form && form.getAttribute('action')) ? form.getAttribute('action') : window.location.pathname;
      const url = new URL(base, window.location.origin);
      const params = new URLSearchParams();

      // 1. Sort By
      const sortEl = document.getElementById(`bbSortSelect-${sectionId}`);
      const formSort = form ? form.querySelector('[name="sort_by"]') : null;
      const sortVal = (sortEl && sortEl.value) || (formSort && formSort.value) || currentSort;
      if (sortVal) {
        params.set('sort_by', sortVal);
      }

      // 2. Form Inputs (Storefront collection.filters parameters)
      if (form) {
        const formData = new FormData(form);
        for (const [key, value] of formData.entries()) {
          if (key === 'sort_by') continue;
          if (value !== '' && value !== null && value !== undefined) {
            params.append(key, value);
          }
        }
      }

      // 3. Ensure any standalone active age pills are serialized
      document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-age-cb-filter:checked`).forEach(input => {
        const paramName = input.getAttribute('name') || 'filter.p.m.custom.age';
        const val = input.value;
        const currentVals = params.getAll(paramName);
        if (!currentVals.includes(val)) {
          params.append(paramName, val);
        }
      });

      // 4. Category Pill Filter (if active and not "all")
      if (currentFilter && currentFilter !== 'all' && currentFilter !== '') {
        params.set('filter.p.category', currentFilter);
      }

      url.search = params.toString();
      return url.toString();
    }

    // Sync input states from current URL params
    function syncInputsFromUrl(targetUrl) {
      try {
        const urlObj = new URL(targetUrl || window.location.href, window.location.origin);
        const searchParams = urlObj.searchParams;

        // Collect URL ages
        const urlAges = [];
        for (const [key, val] of searchParams.entries()) {
          const kDown = key.toLowerCase();
          if (kDown.includes('age') || kDown.includes('bracket') || kDown === 'tag' || kDown === 'filter.p.tag') {
            urlAges.push(val);
          }
        }

        // Collect URL product types
        const urlTypes = [];
        for (const [key, val] of searchParams.entries()) {
          const kDown = key.toLowerCase();
          if (kDown.includes('product_type') || kDown.includes('category') || kDown === 'type') {
            urlTypes.push(val.toLowerCase().trim());
          }
        }

        // Collect URL vendors/brands
        const urlVendors = [];
        for (const [key, val] of searchParams.entries()) {
          const kDown = key.toLowerCase();
          if (kDown.includes('vendor') || kDown.includes('brand')) {
            urlVendors.push(val.toLowerCase().trim());
          }
        }

        // Sync all filter checkboxes from URL params
        document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-facet-checkbox`).forEach(cb => {
          const name = cb.getAttribute('name') || '';
          const nameDown = name.toLowerCase();
          const val = cb.value;
          const valDown = (val || '').toLowerCase().trim();

          let isChecked = false;

          if (name && searchParams.has(name)) {
            const urlVals = searchParams.getAll(name).map(v => v.toLowerCase().trim());
            isChecked = urlVals.includes(valDown);
          } else if ((nameDown.includes('product_type') || nameDown.includes('type') || nameDown.includes('category')) && urlTypes.length > 0) {
            isChecked = urlTypes.includes(valDown);
          } else if ((nameDown.includes('vendor') || nameDown.includes('brand')) && urlVendors.length > 0) {
            isChecked = urlVendors.includes(valDown);
          } else if (cb.classList.contains('bb-age-cb-filter') && urlAges.length > 0) {
            const pill = cb.closest('.bb-filter-age-pill');
            isChecked = urlAges.some(a => {
              const tokens = getAgeTokens(a);
              return tokens.includes(valDown) || tokens.includes((pill?.getAttribute('data-age-label') || '').toLowerCase().trim());
            });
          }

          cb.checked = isChecked;

          const interactiveParent = cb.closest('.bb-filter-age-pill, .bb-filter-size-pill, .bb-filter-swatch-box');
          if (interactiveParent) {
            interactiveParent.classList.toggle('active', cb.checked);
          }
        });

        // Update group badges and clear buttons
        updateAgeFilterControls();
      } catch(e) {}
    }

    // Update age filter UI counters and individual clear buttons
    function updateAgeFilterControls() {
      const activeAgeInputs = Array.from(document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-age-cb-filter:checked`));
      selectedAges = activeAgeInputs.map(cb => cb.value);

      // Also collect active pills if any
      document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-filter-age-pill.active`).forEach(pill => {
        const label = pill.getAttribute('data-age-label');
        if (label && !selectedAges.includes(label)) {
          selectedAges.push(label);
        }
      });

      // Fallback Age group badge & clear button
      const fallbackBadge = document.getElementById(`bbAgeGroupBadge-${sectionId}`);
      const fallbackClear = document.getElementById(`bbAgeGroupClear-${sectionId}`);
      if (fallbackBadge) {
        fallbackBadge.textContent = selectedAges.length;
        fallbackBadge.style.display = selectedAges.length > 0 ? 'inline-flex' : 'none';
      }
      if (fallbackClear) {
        fallbackClear.style.display = selectedAges.length > 0 ? 'inline-block' : 'none';
      }

      // collection.filters age group clear buttons
      document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-filter-group--age`).forEach(group => {
        const groupActive = group.querySelectorAll('.bb-age-cb-filter:checked').length;
        const groupClear = group.querySelector('.bb-filter-group-clear');
        const groupBadge = group.querySelector('.bb-filter-group-badge');
        if (groupClear) {
          groupClear.style.display = groupActive > 0 ? 'inline-block' : 'none';
        }
        if (groupBadge) {
          groupBadge.textContent = groupActive;
          groupBadge.style.display = groupActive > 0 ? 'inline-flex' : 'none';
        }
      });

      // Overall active badges and clear all
      const totalChecked = document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-facet-checkbox:not(.bb-manual-link-checkbox):checked`).length;
      const totalActivePill = document.getElementById(`bbTotalActiveBadge-${sectionId}`);
      const clearAllBtn = document.getElementById(`bbClearAllBtn-${sectionId}`);
      if (totalActivePill) {
        totalActivePill.textContent = totalChecked;
        totalActivePill.style.display = totalChecked > 0 ? 'inline-flex' : 'none';
      }
      if (clearAllBtn) {
        clearAllBtn.style.display = totalChecked > 0 ? 'inline-block' : 'none';
      }
    }

    // AJAX Filter Fetcher with Shopify Section Rendering API
    function performAjaxFilter(targetUrl, pushState = true) {
      if (isFetching) return;
      isFetching = true;

      const grid = document.getElementById(`bbProductsGrid-${sectionId}`);
      const sidebar = document.getElementById(`bbFilterSidebar-${sectionId}`);

      if (grid) grid.classList.add('bb-grid-loading');
      if (sidebar) sidebar.classList.add('bb-sidebar-loading');

      // Construct section fetch URL
      const fetchUrlObj = new URL(targetUrl, window.location.origin);
      fetchUrlObj.searchParams.set('section_id', sectionId);

      fetch(fetchUrlObj.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(response => {
          if (!response.ok) throw new Error('Network response error');
          return response.text();
        })
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          // 1. Update Product Grid
          const newGrid = doc.getElementById(`bbProductsGrid-${sectionId}`);
          const newCards = newGrid ? newGrid.querySelectorAll('.bb-item-card') : [];
          if (newGrid && grid && newCards.length > 0) {
            grid.innerHTML = newGrid.innerHTML;
          }

          // 2. Update Pagination
          const newPag = doc.getElementById(`bbPaginationWrap-${sectionId}`);
          const curPag = document.getElementById(`bbPaginationWrap-${sectionId}`);
          if (newPag && curPag) {
            curPag.innerHTML = newPag.innerHTML;
            curPag.style.display = newPag.style.display;
          }

          // 3. Update Filter Sidebar & Active Facets
          const newSidebar = doc.getElementById(`bbFilterSidebar-${sectionId}`);
          if (newSidebar && sidebar) {
            sidebar.innerHTML = newSidebar.innerHTML;
          }

          // 3b. Update Top Active Facets Bar
          const newTopFacets = doc.getElementById(`bbActiveFacetsTop-${sectionId}`);
          const curTopFacets = document.getElementById(`bbActiveFacetsTop-${sectionId}`);
          if (newTopFacets && curTopFacets) {
            curTopFacets.innerHTML = newTopFacets.innerHTML;
            curTopFacets.style.display = newTopFacets.style.display;
          }

          // 4. Update Product Count Badges
          const newTotalBadge = doc.getElementById('bbProductTotalBadge');
          const curTotalBadge = document.getElementById('bbProductTotalBadge');
          if (newTotalBadge && curTotalBadge) {
            curTotalBadge.textContent = newTotalBadge.textContent;
          }

          const newShown = doc.getElementById(`bbShownCount-${sectionId}`);
          const curShown = document.getElementById(`bbShownCount-${sectionId}`);
          if (newShown && curShown) curShown.textContent = newShown.textContent;

          const newAll = doc.getElementById(`bbAllCount-${sectionId}`);
          const curAll = document.getElementById(`bbAllCount-${sectionId}`);
          if (newAll && curAll) curAll.textContent = newAll.textContent;

          // 5. Update URL History cleanly
          if (pushState) {
            const cleanUrl = new URL(targetUrl, window.location.origin);
            cleanUrl.searchParams.delete('section_id');
            window.history.pushState({ path: cleanUrl.toString() }, '', cleanUrl.toString());
          }

          // 6. Sync inputs and rebind events
          syncInputsFromUrl(targetUrl);
          bindEvents();
          applyFilters();
          window.BlueBuckWishlist?.sync();
        })
        .catch(err => {
          // Fallback to client-side DOM filtering
          if (pushState) {
            const cleanUrl = new URL(targetUrl, window.location.origin);
            cleanUrl.searchParams.delete('section_id');
            window.history.pushState({ path: cleanUrl.toString() }, '', cleanUrl.toString());
          }
          syncInputsFromUrl(targetUrl);
          applyFilters();
          window.BlueBuckWishlist?.sync();
        })
        .finally(() => {
          isFetching = false;
          if (grid) grid.classList.remove('bb-grid-loading');
          if (sidebar) sidebar.classList.remove('bb-sidebar-loading');
        });
    }

    // Client-side DOM filtering (fallback & instant response)
    function applyFilters() {
      const grid = document.getElementById(`bbProductsGrid-${sectionId}`);
      if (!grid) return 0;

      const cards = Array.from(grid.querySelectorAll('.bb-item-card'));
      let visibleCount = 0;

      // Collect active age selections
      updateAgeFilterControls();

      // Collect active size and color selections
      const activeColors = Array.from(document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-color-cb-filter:checked`)).map(c => c.value.toLowerCase().trim());
      const activeSizes = Array.from(document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-size-cb-filter:checked`)).map(s => s.value.toLowerCase().trim());

      // Collect active product type and vendor checkboxes
      const activeTypes = Array.from(document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-facet-checkbox:checked`))
        .filter(cb => {
          const n = (cb.getAttribute('name') || '').toLowerCase();
          return n.includes('product_type') || n.includes('type') || n.includes('category');
        })
        .map(cb => cb.value.toLowerCase().trim());

      const activeVendors = Array.from(document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-facet-checkbox:checked`))
        .filter(cb => {
          const n = (cb.getAttribute('name') || '').toLowerCase();
          return n.includes('vendor') || n.includes('brand');
        })
        .map(cb => cb.value.toLowerCase().trim());

      cards.forEach(card => {
        const title = (card.getAttribute('data-title') || '').toLowerCase();
        const category = (card.getAttribute('data-category') || '').toLowerCase();
        const prodType = (card.getAttribute('data-type') || '').toLowerCase();
        const prodVendor = (card.getAttribute('data-vendor') || '').toLowerCase();
        const tags = (card.getAttribute('data-tags') || '').toLowerCase();
        const price = parseFloat(card.getAttribute('data-price')) || 0;
        const inStock = card.getAttribute('data-in-stock') === 'true';

        let show = true;

        // 1. Interactive Category Pill filter
        if (currentFilter && currentFilter !== 'all' && currentFilter !== '') {
          const f = currentFilter.toLowerCase().trim();
          let matchPill = false;

          if (f === 'girls' || f === 'girl') {
            matchPill = title.includes('girl') || category.includes('girl') || tags.includes('girl') || title.includes('pink') || tags.includes('pink') || tags.includes('frock') || title.includes('frock') || title.includes('dress') || title.includes('skirt');
          } else if (f === 'boys' || f === 'boy') {
            matchPill = title.includes('boy') || category.includes('boy') || tags.includes('boy') || title.includes('polo');
          } else if (f === 't-shirts' || f === 'tshirts' || f === 't-shirt' || f === 'tshirt' || f === 'tees' || f === 'tee') {
            matchPill = title.includes('t-shirt') || title.includes('tshirt') || title.includes('tee') || title.includes('top') || title.includes('shirt') || category.includes('t-shirt') || category.includes('tshirt') || category.includes('top') || category.includes('shirt') || tags.includes('t-shirt') || tags.includes('tshirt') || tags.includes('tee') || tags.includes('top') || tags.includes('shirt');
          } else if (f === 'shorts' || f === 'short') {
            matchPill = title.includes('short') || category.includes('short') || tags.includes('short') || title.includes('pant') || tags.includes('pant') || title.includes('jogger');
          } else if (f === 'sets' || f === 'set') {
            matchPill = title.includes('set') || category.includes('set') || tags.includes('set') || title.includes('suit') || tags.includes('suit') || title.includes('combo') || tags.includes('combo') || title.includes('tracksuit') || title.includes('co-ord') || tags.includes('co-ord');
          } else if (f === 'new-arrivals' || f === 'new arrivals' || f === 'new') {
            matchPill = tags.includes('new') || !!card.querySelector('.bb-item-badge') || title.includes('new');
          } else if (f === 'accessories' || f === 'accessory') {
            matchPill = title.includes('accessori') || category.includes('accessori') || tags.includes('accessori') || title.includes('cap') || title.includes('hat') || title.includes('bag') || title.includes('sock') || title.includes('belt') || tags.includes('cap') || tags.includes('hat') || tags.includes('bag') || tags.includes('sock') || tags.includes('belt');
          } else {
            const tokens = f.split(/[\s-]+/).filter(t => t.length > 1);
            matchPill = tokens.some(t => title.includes(t) || category.includes(t) || tags.includes(t));
          }

          if (!matchPill) {
            show = false;
          }
        }

        // 2. Checkbox category filter
        if (show && selectedCats.length > 0 && selectedCats.length < 5) {
          const matchCat = selectedCats.some(c => {
            if (c === 'girls') return title.includes('girl') || category.includes('girl') || tags.includes('girl');
            if (c === 'boys') return title.includes('boy') || category.includes('boy') || tags.includes('boy');
            if (c === 't-shirts') return title.includes('t-shirt') || title.includes('tshirt') || title.includes('tee') || category.includes('t-shirt') || category.includes('top');
            if (c === 'shorts') return title.includes('short') || category.includes('short') || tags.includes('short');
            if (c === 'sets') return title.includes('set') || category.includes('set') || tags.includes('set') || title.includes('suit') || title.includes('combo');
            return title.includes(c) || category.includes(c) || tags.includes(c);
          });
          if (!matchCat) {
            show = false;
          }
        }

        // 2b. Product Type Checkbox Filter
        if (show && activeTypes.length > 0) {
          const matchType = activeTypes.some(t => {
            const cleanT = t.toLowerCase().trim();
            return prodType.includes(cleanT) || category.includes(cleanT) || title.includes(cleanT) || tags.includes(cleanT);
          });
          if (!matchType) {
            show = false;
          }
        }

        // 2c. Brand / Vendor Checkbox Filter
        if (show && activeVendors.length > 0) {
          const matchVendor = activeVendors.some(v => {
            const cleanV = v.toLowerCase().trim();
            return prodVendor.includes(cleanV) || title.includes(cleanV) || tags.includes(cleanV);
          });
          if (!matchVendor) {
            show = false;
          }
        }

        // 3. Availability filter
        if (show && inStockOnly && !inStock) {
          show = false;
        }

        // 4. Price range filter
        if (show && (price < minPrice || price > maxPrice)) {
          show = false;
        }

        // 5. Age Filter (Matches only products in the selected Age bracket)
        if (show && selectedAges.length > 0) {
          if (!matchesSelectedAges(card, selectedAges)) {
            show = false;
          }
        }

        // 6. Color Swatch Filter
        if (show && activeColors.length > 0) {
          if (!matchesColor(card, activeColors)) {
            show = false;
          }
        }

        // 7. Size Filter
        if (show && activeSizes.length > 0) {
          if (!matchesSize(card, activeSizes)) {
            show = false;
          }
        }

        card.style.display = show ? '' : 'none';
        if (show) visibleCount++;
      });

      // Sort visible cards
      if (currentSort) {
        cards.sort((a, b) => {
          const pA = parseFloat(a.getAttribute('data-price')) || 0;
          const pB = parseFloat(b.getAttribute('data-price')) || 0;
          const tA = a.getAttribute('data-title') || '';
          const tB = b.getAttribute('data-title') || '';

          if (currentSort === 'price-ascending') return pA - pB;
          if (currentSort === 'price-descending') return pB - pA;
          if (currentSort === 'title-ascending') return tA.localeCompare(tB);
          if (currentSort === 'title-descending') return tB.localeCompare(tA);
          return 0;
        });
        cards.forEach(card => grid.appendChild(card));
      }

      // Empty State Display
      const emptyMsg = document.getElementById(`bbEmptyGridMsg-${sectionId}`);
      if (emptyMsg) {
        emptyMsg.style.display = visibleCount === 0 ? 'block' : 'none';
      }

      // Pagination Controls Display
      const pagWrap = document.getElementById(`bbPaginationWrap-${sectionId}`);
      if (pagWrap) {
        pagWrap.style.display = visibleCount === 0 ? 'none' : '';
      }

      // Update counters
      const countBadge = document.getElementById('bbProductTotalBadge');
      const shownCount = document.getElementById(`bbShownCount-${sectionId}`);
      const allCount = document.getElementById(`bbAllCount-${sectionId}`);

      if (countBadge) countBadge.textContent = `${visibleCount} products`;
      if (shownCount) shownCount.textContent = visibleCount;
      if (allCount) allCount.textContent = cards.length;

      return visibleCount;
    }

    // Quick View Drawer Controller
    function openQuickView(id) {
      let productData = null;
      const dataEl = document.getElementById(`bbProductData-${id}`);
      if (dataEl) {
        try {
          productData = JSON.parse(dataEl.textContent);
        } catch(e) {}
      }

      if (!productData) {
        productData = {
          id: id,
          title: 'Wavy Pink Girl’s T-Shirt',
          salePrice: 849,
          originalPrice: 1499,
          discount: '43% OFF',
          description: 'Crafted from ultra-soft 4-way stretch fabric for maximum agility and breathable everyday performance.',
          media: [
            { type: 'image', src: 'https://images.unsplash.com/photo-1518831959646-742c3a14ebf7?w=1000&auto=format&fit=crop&q=80' },
            { type: 'image', src: 'https://images.unsplash.com/photo-1543332164-6e82f355badc?w=1000&auto=format&fit=crop&q=80' },
            { type: 'video', src: 'https://assets.mixkit.co/videos/preview/mixkit-little-boy-playing-with-a-soccer-ball-41551-large.mp4', poster: 'https://images.unsplash.com/photo-1518831959646-742c3a14ebf7?w=1000&auto=format&fit=crop&q=80' }
          ]
        };
      }

      currentQvItem = productData;
      activeMediaIndex = 0;

      const modal = document.getElementById(`bbQvModal-${sectionId}`);
      const backdrop = document.getElementById(`bbQvBackdrop-${sectionId}`);
      const title = document.getElementById(`bbQvTitle-${sectionId}`);
      const sale = document.getElementById(`bbQvSale-${sectionId}`);
      const compare = document.getElementById(`bbQvCompare-${sectionId}`);
      const save = document.getElementById(`bbQvSave-${sectionId}`);
      const discount = document.getElementById(`bbQvDiscount-${sectionId}`);
      const desc = document.getElementById(`bbQvDesc-${sectionId}`);

      if (title) title.textContent = (productData.title || '').toUpperCase();
      if (sale) sale.textContent = `₹${productData.salePrice.toLocaleString('en-IN')}.00`;
      if (compare) compare.textContent = productData.originalPrice > productData.salePrice ? `₹${productData.originalPrice.toLocaleString('en-IN')}.00` : '';
      if (save) {
        const diff = productData.originalPrice - productData.salePrice;
        save.textContent = diff > 0 ? `SAVE RS. ${diff.toLocaleString('en-IN')}.00` : '';
        save.style.display = diff > 0 ? 'inline-block' : 'none';
      }
      if (discount) {
        discount.textContent = productData.discount || '43% OFF';
        discount.style.display = productData.discount ? 'inline-block' : 'none';
      }
      if (desc && productData.description) desc.textContent = productData.description;

      renderQvStage();
      renderQvThumbs();
      renderQvRecs();

      if (modal && backdrop) {
        modal.classList.add('active');
        backdrop.classList.add('active');
        document.body.style.overflow = 'hidden';
      }
    }

    function closeQuickView() {
      const modal = document.getElementById(`bbQvModal-${sectionId}`);
      const backdrop = document.getElementById(`bbQvBackdrop-${sectionId}`);
      if (modal) modal.classList.remove('active');
      if (backdrop) backdrop.classList.remove('active');
      document.body.style.overflow = '';

      const stage = document.getElementById(`bbQvStage-${sectionId}`);
      if (stage) stage.querySelectorAll('video').forEach(v => v.pause());
    }

    function renderQvStage() {
      const stage = document.getElementById(`bbQvStage-${sectionId}`);
      if (!stage || !currentQvItem || !currentQvItem.media || currentQvItem.media.length === 0) return;

      const media = currentQvItem.media[activeMediaIndex] || currentQvItem.media[0];

      if (media.type === 'video') {
        stage.innerHTML = `
          <video class="bb-qv-stage-video" controls playsinline preload="metadata" poster="${media.poster || ''}">
            <source src="${media.src}" type="video/mp4">
            Your browser does not support HTML5 video.
          </video>
        `;
      } else if (media.type === 'external_video') {
        stage.innerHTML = `
          <iframe
            src="https://www.youtube.com/embed/${media.src}?enablejsapi=1"
            class="bb-qv-stage-video"
            frameborder="0"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        `;
      } else {
        stage.innerHTML = `
          <img src="${media.src}" alt="${currentQvItem.title}" class="bb-qv-stage-img">
        `;
      }
    }

    function renderQvThumbs() {
      const track = document.getElementById(`bbQvThumbsStrip-${sectionId}`);
      if (!track || !currentQvItem || !currentQvItem.media) return;

      let html = '';
      currentQvItem.media.forEach((m, idx) => {
        const thumb = (m.type === 'video' || m.type === 'external_video') ? (m.poster || m.src) : m.src;
        const isVideo = m.type === 'video' || m.type === 'external_video';
        html += `
          <button
            type="button"
            class="bb-qv-thumb-item-btn ${idx === activeMediaIndex ? 'active' : ''} ${isVideo ? 'bb-qv-thumb-video' : ''}"
            data-qv-thumb-idx="${idx}"
            aria-label="${isVideo ? 'Product Video' : 'Thumbnail ' + (idx + 1)}"
          >
            <img src="${thumb}" alt="${isVideo ? 'Video' : 'Thumb ' + (idx + 1)}" class="bb-qv-thumb-item-img">
            ${isVideo ? '<span class="bb-qv-thumb-play-tag">▶ VIDEO</span>' : ''}
          </button>
        `;
      });
      track.innerHTML = html;
    }

    function renderQvRecs() {
      const track = document.getElementById(`bbQvRecsTrack-${sectionId}`);
      if (!track) return;

      const cards = Array.from(document.querySelectorAll('.bb-item-card')).slice(0, 4);
      let html = '';
      cards.forEach(c => {
        const id = c.getAttribute('data-product-id');
        const title = c.querySelector('.bb-item-name')?.textContent || 'Related item';
        const price = c.querySelector('.bb-item-sale-price')?.textContent || '₹849';
        const img = c.querySelector('.bb-item-img-primary')?.getAttribute('src') || '';

        html += `
          <div class="bb-rec-item-card">
            <div class="bb-rec-media-box">
              <img src="${img}" alt="${title}" class="bb-rec-img-src" loading="lazy">
            </div>
            <h4 class="bb-rec-item-name">${title}</h4>
            <span class="bb-rec-item-price">${price}</span>
            <button type="button" class="bb-rec-trigger-btn" data-open-qv="${id}">
              CHOOSE OPTIONS
            </button>
          </div>
        `;
      });
      track.innerHTML = html;
    }

    function bindEvents() {
      const filterForm = document.getElementById(`bbFacetFiltersForm-${sectionId}`);

      // 1. Facet Form Changes (Checkboxes, Radios)
      if (filterForm) {
        filterForm.addEventListener('change', function(e) {
          if (e.target.classList.contains('bb-facet-price-input') || e.target.classList.contains('bb-price-slider-native') || e.target.classList.contains('bb-manual-link-checkbox')) {
            return; // handled by debounced price listeners or manual links
          }

          // Update pill/swatch active class if target was inside an interactive filter
          const pill = e.target.closest('.bb-filter-age-pill, .bb-filter-size-pill, .bb-filter-swatch-box');
          if (pill) {
            pill.classList.toggle('active', e.target.checked);
          }

          // Update local state helpers
          selectedCats = Array.from(document.querySelectorAll('.bb-cat-cb-filter:checked')).map(c => c.value);
          const inStockEl = document.getElementById(`bbInStockCb-${sectionId}`);
          if (inStockEl) inStockOnly = inStockEl.checked;

          updateAgeFilterControls();
          performAjaxFilter(getFilterFormUrl());
        });

        filterForm.addEventListener('submit', function(e) {
          e.preventDefault();
          performAjaxFilter(getFilterFormUrl());
        });
      }

      // 2. Interactive Filter Pill & Swatch Click Helpers
      document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-filter-age-pill, #bbFilterSidebar-${sectionId} .bb-filter-size-pill, #bbFilterSidebar-${sectionId} .bb-filter-swatch-box`).forEach(el => {
        el.addEventListener('click', function(e) {
          const input = this.querySelector('input[type="checkbox"]');
          if (e.target !== input && input) {
            e.preventDefault();
            input.checked = !input.checked;
            this.classList.toggle('active', input.checked);
            updateAgeFilterControls();
            performAjaxFilter(getFilterFormUrl());
          }
        });
      });

      // 3. Individual Filter Group Clear Buttons (e.g., Clear Age/Size/Color filters)
      document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-filter-group-clear`).forEach(btn => {
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          const group = this.closest('.bb-filter-group');
          if (group) {
            group.querySelectorAll('input[type="checkbox"]').forEach(cb => {
              cb.checked = false;
            });
            group.querySelectorAll('.bb-filter-age-pill, .bb-filter-size-pill, .bb-filter-swatch-box').forEach(pill => {
              pill.classList.remove('active');
            });
            this.style.display = 'none';
            const badge = group.querySelector('.bb-filter-group-badge');
            if (badge) badge.style.display = 'none';
          }
          updateAgeFilterControls();
          performAjaxFilter(getFilterFormUrl());
        });
      });

      // 4. Interactive Category Pills
      document.querySelectorAll(`#bbPillsTrack-${sectionId} .bb-category-pill-btn`).forEach(btn => {
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          document.querySelectorAll(`#bbPillsTrack-${sectionId} .bb-category-pill-btn`).forEach(b => b.classList.remove('active'));
          this.classList.add('active');

          const handle = (this.getAttribute('data-filter-handle') || '').toLowerCase().trim();
          const label = (this.getAttribute('data-filter-label') || this.textContent || '').toLowerCase().trim();

          currentFilter = handle || label;
          performAjaxFilter(getFilterFormUrl());
        });
      });

      // 5. Clear All Buttons - Completely removes all search query parameters from URL and reloads unfiltered collection
      const clearBtn = document.getElementById(`bbClearAllBtn-${sectionId}`);
      const clearBtnTop = document.getElementById(`bbClearAllBtnTop-${sectionId}`);

      function handleClearAll(e) {
        if (e) e.preventDefault();
        currentFilter = 'all';
        currentSort = defaultSort;
        inStockOnly = true;
        minPrice = 0;
        maxPrice = 5000;
        selectedCats = ['girls', 'boys', 't-shirts', 'shorts', 'sets'];
        selectedAges = [];

        // Uncheck all checkboxes in filter form
        if (filterForm) {
          filterForm.querySelectorAll('input[type="checkbox"]').forEach(cb => {
            cb.checked = false;
          });
        }

        // Remove active classes from all pills & swatches
        document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-filter-age-pill, #bbFilterSidebar-${sectionId} .bb-filter-size-pill, #bbFilterSidebar-${sectionId} .bb-filter-swatch-box`).forEach(p => {
          p.classList.remove('active');
        });

        // Reset Category pills to first (All)
        document.querySelectorAll(`#bbPillsTrack-${sectionId} .bb-category-pill-btn`).forEach((b, i) => b.classList.toggle('active', i === 0));

        // Hide individual clear buttons & badges
        document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-filter-group-clear`).forEach(b => b.style.display = 'none');
        document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-filter-group-badge`).forEach(b => b.style.display = 'none');

        // Clean collection URL without search params
        const clearUrl = (formActionBase() || window.location.pathname);
        window.history.pushState({ path: clearUrl }, '', clearUrl);
        performAjaxFilter(clearUrl);
      }

      if (clearBtn) clearBtn.addEventListener('click', handleClearAll);
      if (clearBtnTop) clearBtnTop.addEventListener('click', handleClearAll);
      document.querySelectorAll('.bb-active-facet-clear-all').forEach(btn => btn.addEventListener('click', handleClearAll));

      function formActionBase() {
        const f = document.getElementById(`bbFacetFiltersForm-${sectionId}`);
        return (f && f.getAttribute('action')) ? f.getAttribute('action') : window.location.pathname;
      }

      // 6. Active Facet Removal Tags / Pills (Top bar + Sidebar)
      document.querySelectorAll(`#bbActiveFacetsTop-${sectionId} .bb-active-facet-pill, #bbActiveFacetsTop-${sectionId} .bb-active-facet-tag, #bbActiveFacets-${sectionId} .bb-active-facet-pill, #bbActiveFacets-${sectionId} .bb-active-facet-tag`).forEach(tag => {
        tag.addEventListener('click', function(e) {
          e.preventDefault();
          const removeUrl = this.getAttribute('data-remove-url') || this.href;
          const paramName = this.getAttribute('data-param-name');
          const paramVal = this.getAttribute('data-param-value');

          if (paramName && filterForm) {
            const input = filterForm.querySelector(`input[name="${paramName}"][value="${paramVal}"]`);
            if (input) {
              input.checked = false;
              const pill = input.closest('.bb-filter-age-pill, .bb-filter-size-pill, .bb-filter-swatch-box');
              if (pill) pill.classList.remove('active');
            }
          }

          if (removeUrl && removeUrl !== '#' && removeUrl !== '') {
            performAjaxFilter(removeUrl);
          } else {
            performAjaxFilter(getFilterFormUrl());
          }
        });
      });

      // 7. Empty State Reset Button
      const emptyResetBtn = document.getElementById(`bbEmptyResetBtn-${sectionId}`);
      if (emptyResetBtn) {
        emptyResetBtn.addEventListener('click', () => {
          if (clearBtn) {
            clearBtn.click();
          } else {
            currentFilter = 'all';
            selectedAges = [];
            document.querySelectorAll(`#bbFilterSidebar-${sectionId} .bb-filter-age-pill`).forEach(p => p.classList.remove('active'));
            document.querySelectorAll(`#bbFilterSidebar-${sectionId} input[type="checkbox"]`).forEach(cb => cb.checked = false);
            document.querySelectorAll(`#bbPillsTrack-${sectionId} .bb-category-pill-btn`).forEach((b, i) => {
              b.classList.toggle('active', i === 0);
            });
            const cleanUrl = window.location.pathname;
            window.history.pushState({ path: cleanUrl }, '', cleanUrl);
            performAjaxFilter(cleanUrl);
          }
        });
      }

      // 8. Sort Select
      const sortEl = document.getElementById(`bbSortSelect-${sectionId}`);
      if (sortEl) {
        sortEl.addEventListener('change', function() {
          currentSort = this.value;
          const formSort = document.getElementById(`bbFacetSortBy-${sectionId}`);
          if (formSort) formSort.value = this.value;
          performAjaxFilter(getFilterFormUrl());
        });
      }

      // 9. Price Slider & Number Inputs with Debounce
      const slider = document.getElementById(`bbPriceSlider-${sectionId}`);
      const minInput = document.getElementById(`bbPriceMinInput-${sectionId}`);
      const maxInput = document.getElementById(`bbPriceMaxInput-${sectionId}`);
      const maxLabel = document.getElementById(`bbPriceMaxLabel-${sectionId}`);
      const minLabel = document.getElementById(`bbPriceMinLabel-${sectionId}`);

      const onPriceInputDebounced = () => {
        clearTimeout(priceDebounceTimer);
        priceDebounceTimer = setTimeout(() => {
          performAjaxFilter(getFilterFormUrl());
        }, 380);
      };

      if (slider && maxInput) {
        slider.addEventListener('input', function() {
          maxPrice = parseFloat(this.value) || 5000;
          maxInput.value = maxPrice;
          if (maxLabel) maxLabel.textContent = `₹${maxPrice.toLocaleString('en-IN')}`;
          applyFilters();
          onPriceInputDebounced();
        });

        maxInput.addEventListener('input', function() {
          maxPrice = parseFloat(this.value) || 5000;
          slider.value = maxPrice;
          if (maxLabel) maxLabel.textContent = `₹${maxPrice.toLocaleString('en-IN')}`;
          applyFilters();
          onPriceInputDebounced();
        });

        if (minInput) {
          minInput.addEventListener('input', function() {
            minPrice = parseFloat(this.value) || 0;
            if (minLabel) minLabel.textContent = `₹${minPrice.toLocaleString('en-IN')}`;
            applyFilters();
            onPriceInputDebounced();
          });
        }
      }

      // 10. Mobile Filter Drawer Toggle & Apply
      const mobTrigger = document.getElementById(`bbMobFilterBtn-${sectionId}`);
      const sidebar = document.getElementById(`bbFilterSidebar-${sectionId}`);
      const overlay = document.getElementById(`bbFilterOverlay-${sectionId}`);
      const closeFilter = document.getElementById(`bbFilterCloseBtn-${sectionId}`);
      const applyMob = document.getElementById(`bbApplyMobBtn-${sectionId}`);

      const toggleFilter = (open) => {
        if (!sidebar || !overlay) return;
        sidebar.classList.toggle('active', open);
        overlay.classList.toggle('active', open);
      };

      if (mobTrigger) mobTrigger.onclick = () => toggleFilter(true);
      if (closeFilter) closeFilter.onclick = () => toggleFilter(false);
      if (overlay) overlay.onclick = () => toggleFilter(false);
      if (applyMob) {
        applyMob.onclick = (e) => {
          e.preventDefault();
          toggleFilter(false);
          performAjaxFilter(getFilterFormUrl());
        };
      }
    }

    // Initialize section
    syncInputsFromUrl(window.location.href);
    bindEvents();
    applyFilters();

    // Browser History Back/Forward AJAX Navigation
    window.addEventListener('popstate', function() {
      syncInputsFromUrl(window.location.href);
      performAjaxFilter(window.location.href, false);
    });

    // Global Delegation for Quick Add, Quick View, Wishlist & Thumbs
    document.addEventListener('click', function(e) {
      const quickAddBtn = e.target.closest('.bb-card-quick-add-btn');
      if (quickAddBtn) {
        e.preventDefault();
        e.stopPropagation();
        const variantId = quickAddBtn.getAttribute('data-quick-add-id');
        const defaultLabel = quickAddBtn.getAttribute('data-default-label') || 'CHOOSE OPTIONS';
        if (!variantId) return;

        quickAddBtn.textContent = 'ADDING...';
        quickAddBtn.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1
          })
        })
          .then(res => res.json())
          .then(item => {
            quickAddBtn.textContent = 'ADDED!';
            quickAddBtn.style.background = '#16a34a';
            quickAddBtn.style.color = '#ffffff';

            fetch('/cart.js')
              .then(r => r.json())
              .then(cart => {
                const badgeSelectors = [
                  '.cart-badge-count',
                  '.bluebuck-cart-badge',
                  '.cart-count-bubble',
                  '[data-cart-count]',
                  '.cart-count',
                  '.bb-cart-count',
                  '#cart-count-mobile',
                  '.header__cart-count',
                  '#cart-icon-bubble span',
                  '.cart-bubble__text-count'
                ];
                badgeSelectors.forEach(sel => {
                  document.querySelectorAll(sel).forEach(badge => {
                    badge.textContent = cart.item_count;
                    if (cart.item_count > 0) {
                      badge.style.display = '';
                      badge.classList.remove('visually-hidden');
                    } else {
                      badge.style.display = 'none';
                    }
                  });
                });
                document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
                document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
                document.dispatchEvent(new CustomEvent('theme:cart:add', { detail: item }));
              }).catch(() => {});

            setTimeout(() => {
              quickAddBtn.textContent = defaultLabel;
              quickAddBtn.disabled = false;
              quickAddBtn.style.background = '';
              quickAddBtn.style.color = '';
            }, 1500);
          })
          .catch(err => {
            quickAddBtn.textContent = defaultLabel;
            quickAddBtn.disabled = false;
          });
        return;
      }

      const qvTrigger = e.target.closest('[data-open-qv]');
      if (qvTrigger) {
        e.preventDefault();
        openQuickView(qvTrigger.getAttribute('data-open-qv'));
        return;
      }

      const wishTrigger = e.target.closest('[data-wish-id]');
      if (wishTrigger) {
        e.preventDefault();
        e.stopPropagation();
        const pId = wishTrigger.getAttribute('data-wish-id');
        let list = getWishlist();
        if (list.includes(pId)) {
          list = list.filter(i => i !== pId);
          wishTrigger.classList.remove('active');
        } else {
          list.push(pId);
          wishTrigger.classList.add('active');
        }
        setWishlist(list);
        return;
      }

      const thumb = e.target.closest('[data-qv-thumb-idx]');
      if (thumb) {
        activeMediaIndex = parseInt(thumb.getAttribute('data-qv-thumb-idx'), 10) || 0;
        document.querySelectorAll('.bb-qv-thumb-item-btn').forEach((b, i) => {
          b.classList.toggle('active', i === activeMediaIndex);
        });
        renderQvStage();
        return;
      }
    });

    // Quick View Controls
    const qvClose = document.getElementById(`bbQvClose-${sectionId}`);
    const qvBackdrop = document.getElementById(`bbQvBackdrop-${sectionId}`);
    if (qvClose) qvClose.addEventListener('click', closeQuickView);
    if (qvBackdrop) qvBackdrop.addEventListener('click', closeQuickView);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeQuickView();
    });

    // Quick View Arrows
    const arrowPrev = document.getElementById(`bbQvArrowPrev-${sectionId}`);
    const arrowNext = document.getElementById(`bbQvArrowNext-${sectionId}`);
    if (arrowPrev) {
      arrowPrev.addEventListener('click', () => {
        if (!currentQvItem || !currentQvItem.media) return;
        activeMediaIndex = (activeMediaIndex - 1 + currentQvItem.media.length) % currentQvItem.media.length;
        renderQvStage();
        renderQvThumbs();
      });
    }
    if (arrowNext) {
      arrowNext.addEventListener('click', () => {
        if (!currentQvItem || !currentQvItem.media) return;
        activeMediaIndex = (activeMediaIndex + 1) % currentQvItem.media.length;
        renderQvStage();
        renderQvThumbs();
      });
    }

    // Stepper
    const decr = document.getElementById(`bbQtyDecr-${sectionId}`);
    const incr = document.getElementById(`bbQtyIncr-${sectionId}`);
    const qty = document.getElementById(`bbQtyVal-${sectionId}`);
    if (decr && qty) {
      decr.addEventListener('click', () => {
        let v = parseInt(qty.value, 10) || 1;
        if (v > 1) qty.value = v - 1;
      });
    }
    if (incr && qty) {
      incr.addEventListener('click', () => {
        let v = parseInt(qty.value, 10) || 1;
        qty.value = v + 1;
      });
    }

    // Add to Cart
    const addCartBtn = document.getElementById(`bbQvAddCartBtn-${sectionId}`);
    if (addCartBtn) {
      addCartBtn.addEventListener('click', () => {
        addCartBtn.textContent = 'ADDED ✓';
        addCartBtn.disabled = true;
        setTimeout(() => {
          addCartBtn.textContent = 'ADD TO CART';
          addCartBtn.disabled = false;
          closeQuickView();
        }, 900);
      });
    }

    // Buy It Now
    const buyNowBtn = document.getElementById(`bbQvBuyNowBtn-${sectionId}`);
    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', () => {
        location.href = '/checkout';
      });
    }

    // Recs Navigation
    const rPrev = document.getElementById(`bbRecsPrev-${sectionId}`);
    const rNext = document.getElementById(`bbRecsNext-${sectionId}`);
    const rTrack = document.getElementById(`bbQvRecsTrack-${sectionId}`);
    if (rPrev && rTrack) rPrev.addEventListener('click', () => rTrack.scrollBy({ left: -150, behavior: 'smooth' }));
    if (rNext && rTrack) rNext.addEventListener('click', () => rTrack.scrollBy({ left: 150, behavior: 'smooth' }));
  }

  function initAll() {
    document.querySelectorAll('.bb-collection-section').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify Theme Editor live reload support
  document.addEventListener('shopify:section:load', function(e) {
    const sec = e.target.querySelector('.bb-collection-section') || (e.target.classList.contains('bb-collection-section') ? e.target : null);
    if (sec) initSection(sec);
  });
})();
